import { StaticImage } from "gatsby-plugin-image"
import React, { useContext, useState } from "react"
import ContentDiv from "../components/contentDiv"
import Layout from "../components/layout"
import PageImage from "../components/pageImage"
import PageSection from "../components/pageSection"
import UnfoldBoxList, { ListItem } from "../components/unfoldBoxList"
import Tiles from "../components/tiles"
import FirstSection from "../components/firstSection"
import styled from "styled-components"
import Text, { TextType } from "../components/text"
import Anforderungen from "../icons/anforderungen.svg"
import DasBietenWir from "../icons/das-bieten-wir.svg"
import DasErwartenWir from "../icons/das-erwarten-wir.svg"
import Aufgaben from "../icons/aufgaben.svg"
import { laptop } from "../util/mediaQueries"
import JobOffering from "../types/jobOffering"
import { BackendContext } from "../services"
import { Media } from "gatsby-plugin-fresnel"
import SEO from "../components/seo"

const StyledUl = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 10px;
  padding-left: 20px;
  margin-top: 0;
  min-height: 100%;
  height: 100%;
`

const StyledLi = styled.li`
  font-size: 12px;
`

const OffersDiv = styled.div`
  @media ${laptop} {
    margin-right: 0;
    margin-left: 0;
  }
`

const AnforderungenIcon = styled(Anforderungen)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const DasBietenWirIcon = styled(DasBietenWir)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const DasErwartenWirIcon = styled(DasErwartenWir)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`
const AufgabenIcon = styled(Aufgaben)`
  height: ${props => props.theme.subHeaderSize};
  width: ${props => props.theme.subHeaderSize};
  max-height: ${props => props.theme.subHeaderSize};
  min-width: ${props => props.theme.subHeaderSize};
  margin-right: 20px;
`

const QuizContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin: 20px 0;
  flex-wrap: wrap;
  background: white;
  border: 2px solid ${props => props.theme.main};
  border-radius: 15px;
  padding: 25px;
  box-shadow: ${props => props.theme.shadow};
`

const QuizLabel = styled.div`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  text-align: center;
`

const QuizButton = styled.button`
  display: inline-block;
  text-decoration: none;
  color: white;
  background: ${props => props.theme.main};
  padding: 15px 30px;
  border-radius: 10px;
  font-size: ${props => props.theme.subHeaderSize};
  font-weight: bold;
  text-align: center;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowHovered};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const PopupContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  max-width: 600px;
  min-width: 300px;
  width: 90%;
  box-shadow: ${props => props.theme.shadowHovered};
  text-align: center;
`

const PopupTitle = styled.h3`
  color: ${props => props.theme.main};
  font-size: ${props => props.theme.subHeaderSize};
  margin-bottom: 15px;
`

const PopupText = styled.p`
  color: ${props => props.theme.textColor};
  font-size: ${props => props.theme.textSize};
  margin-bottom: 25px;
  line-height: 1.5;
`

const PopupButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`

const PopupButton = styled.button`
  padding: 12px 25px;
  border-radius: 8px;
  font-size: ${props => props.theme.textSize};
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-1px);
  }
`

const CancelButton = styled(PopupButton)`
  background: ${props => props.theme.second};
  color: ${props => props.theme.textColor};
  
  &:hover {
    background: ${props => props.theme.third};
  }
`

const OkButton = styled(PopupButton)`
  background: ${props => props.theme.main};
  color: white;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowHovered};
  }
`

const getBodyUl = (lines: string[]) => (
  <StyledUl>
    {lines.map((line, index) => (
      <StyledLi key={index}>
        <Text type={TextType.Body}>{line}</Text>
      </StyledLi>
    ))}
  </StyledUl>
)

const MwdText = () => <Text type={TextType.Body}>(m/w/d)</Text>

const getJobBox: (
  screenType: string
) => (desc: JobOffering) => ListItem = screenType => desc => {
  const tileList = [
    {
      title: "Aufgaben",
      icon: <AufgabenIcon />,
      body: getBodyUl(desc.aufgaben),
    },
    {
      title: "Anforderungen",
      icon: <AnforderungenIcon />,
      body: getBodyUl(desc.anforderungen),
    },
    {
      title: "Das erwarten wir",
      icon: <DasErwartenWirIcon />,
      body: getBodyUl(desc.dasErwartenWir),
    },
    {
      title: "Das bieten wir",
      icon: <DasBietenWirIcon />,
      body: getBodyUl(desc.dasBietenWir),
    },
  ]
  const maxHeight = `${
    screenType === "lg" || screenType === "xl"
      ? desc.maxHeightLarge
      : screenType === "md"
      ? desc.maxHeightTablet
      : desc.maxHeightPhone
  }px`

  return {
    title: desc.name,
    body: <Tiles tileList={tileList} />,
    icon: [<MwdText />, <MwdText />],
    maxHeight: maxHeight,
  }
}

const Karriere = () => {
  const { jobOfferings } = useContext(BackendContext)
  const [showPopup, setShowPopup] = useState(false)

  const handleGoClick = () => {
    setShowPopup(true)
  }

  const handleCancel = () => {
    setShowPopup(false)
  }

  const handleOk = () => {
    window.open("https://bewerbung.dietrichsuess.de", "_blank", "noopener,noreferrer")
    setShowPopup(false)
  }
  return (
    <Layout>
      <PageImage>
        <Media greaterThanOrEqual="md">
          <StaticImage
            src="../images/Titelbild_Karriere_PC.jpeg"
            quality={95}
            formats={["AUTO", "WEBP", "AVIF"]}
            alt="Image"
            objectFit="contain"
            layout="fullWidth"
          />
        </Media>
        <Media at="sm">
          <StaticImage
            src="../images/Titelbild_Karriere_Smartp.jpeg"
            quality={95}
            formats={["AUTO", "WEBP", "AVIF"]}
            alt="Image"
            objectFit="contain"
            layout="fullWidth"
          />
        </Media>
      </PageImage>
      <FirstSection headerText="Unsere aktuellen Stellenangebote">
        Wir sind auf der Suche nach engagierten Mitarbeitern, die mit uns
        gemeinsam unsere Mandanten optimal betreuen und am Erfolg teilhaben
        wollen.
      </FirstSection>
      <QuizContainer>
        <QuizLabel>
          In nur 2 Minuten bei uns bewerben
        </QuizLabel>
        <QuizButton onClick={handleGoClick}>
          Go
        </QuizButton>
      </QuizContainer>
      
      {showPopup && (
        <PopupOverlay onClick={handleCancel}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <PopupTitle>Weiterleitung zu externer Website</PopupTitle>
            <PopupText>
              Sie werden zu einer externen Website weitergeleitet, um sich bei uns zu bewerben. 
              Möchten Sie fortfahren?
            </PopupText>
            <PopupButtons>
              <CancelButton onClick={handleCancel}>
                Abbrechen
              </CancelButton>
              <OkButton onClick={handleOk}>
                OK
              </OkButton>
            </PopupButtons>
          </PopupContent>
        </PopupOverlay>
      )}
      <PageSection>
        <ContentDiv>
          <OffersDiv>
            <Media greaterThanOrEqual="lg">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("lg"))} />
            </Media>
            <Media at="md">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("md"))} />
            </Media>
            <Media at="sm">
              <UnfoldBoxList items={jobOfferings.map(getJobBox("sm"))} />
            </Media>
          </OffersDiv>
        </ContentDiv>
      </PageSection>
    </Layout>
  )
}

// Gatsby Head API - modern way to manage document head
export const Head = () => {
  const seoData = SEO({ lang: "de", title: "Karriere" })
  
  return (
    <>
      <html lang={seoData.lang} />
      <title>{seoData.titleTemplate ? seoData.titleTemplate.replace('%s', seoData.title) : seoData.title}</title>
      {seoData.meta.map((meta, index) => (
        <meta key={index} name={meta.name} property={meta.property} content={meta.content} />
      ))}
    </>
  )
}

export default Karriere
